import React, { useEffect, useState } from 'react';
import { FaFilter, FaRotateLeft } from 'react-icons/fa6';
import {
    COMPLETION_OPTIONS,
    STATUS_OPTIONS,
    previousMonthDefaults,
} from './refundAuditFilters';

export interface RefundAuditFilterValues {
    from_date: string;
    to_date: string;
    status: string;
    completion_status: string;
}

interface Props {
    /** The filters the current results were fetched with. */
    applied: RefundAuditFilterValues;
    onApply: (values: RefundAuditFilterValues) => void;
    disabled?: boolean;
}

const inputClass =
    'h-10 rounded border border-stroke px-3 text-sm outline-none focus:border-primary';
const labelClass = 'mb-1 block text-[12px] font-bold';

/**
 * Re-runs the refund audit without a round trip through the report card. Edits
 * are held locally and applied on submit, so changing a date range and a status
 * costs one fetch rather than one per keystroke.
 */
const RefundAuditFilterBar: React.FC<Props> = ({ applied, onApply, disabled }) => {
    const [draft, setDraft] = useState<RefundAuditFilterValues>(applied);

    // Re-sync when the applied filters change from outside the bar — browser
    // back/forward, or the dates the API filled in for an unfiltered request.
    // Depends on the values rather than the object the parent rebuilds every
    // render, which would otherwise wipe the draft mid-edit.
    const { from_date, to_date, status, completion_status } = applied;
    useEffect(() => {
        setDraft({ from_date, to_date, status, completion_status });
    }, [from_date, to_date, status, completion_status]);

    const set = (key: keyof RefundAuditFilterValues, value: string) =>
        setDraft((prev) => ({ ...prev, [key]: value }));

    const invalidRange =
        !!draft.from_date && !!draft.to_date && draft.from_date > draft.to_date;

    const dirty =
        draft.from_date !== applied.from_date ||
        draft.to_date !== applied.to_date ||
        draft.status !== applied.status ||
        draft.completion_status !== applied.completion_status;

    const defaults = previousMonthDefaults();
    const atDefaults =
        applied.from_date === defaults.from &&
        applied.to_date === defaults.to &&
        !applied.status &&
        !applied.completion_status;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (invalidRange) return;
        onApply(draft);
    };

    const handleReset = () =>
        onApply({
            from_date: defaults.from,
            to_date: defaults.to,
            status: '',
            completion_status: '',
        });

    return (
        <form onSubmit={handleSubmit}>
            <div className='flex flex-wrap items-end gap-3'>
                <div>
                    <label className={labelClass} htmlFor='refund-from'>
                        From
                    </label>
                    <input
                        id='refund-from'
                        type='date'
                        value={draft.from_date}
                        onChange={(e) => set('from_date', e.target.value)}
                        className={inputClass}
                    />
                </div>
                <div>
                    <label className={labelClass} htmlFor='refund-to'>
                        To
                    </label>
                    <input
                        id='refund-to'
                        type='date'
                        value={draft.to_date}
                        onChange={(e) => set('to_date', e.target.value)}
                        className={inputClass}
                    />
                </div>
                <div>
                    <label className={labelClass} htmlFor='refund-status'>
                        Refund Status
                    </label>
                    <select
                        id='refund-status'
                        value={draft.status}
                        onChange={(e) => set('status', e.target.value)}
                        className={`${inputClass} bg-white`}
                    >
                        <option value=''>Any status</option>
                        {STATUS_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                                {o.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className={labelClass} htmlFor='refund-completion'>
                        RE/MIP Entered
                    </label>
                    <select
                        id='refund-completion'
                        value={draft.completion_status}
                        onChange={(e) => set('completion_status', e.target.value)}
                        className={`${inputClass} bg-white`}
                    >
                        <option value=''>Any</option>
                        {COMPLETION_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                                {o.label}
                            </option>
                        ))}
                    </select>
                </div>
                <button
                    type='submit'
                    disabled={disabled || invalidRange || !dirty}
                    className='inline-flex h-10 items-center gap-2 rounded-lg bg-primary px-5 text-sm font-medium text-white disabled:opacity-50'
                >
                    <FaFilter size={12} /> Apply
                </button>
                {!atDefaults && (
                    <button
                        type='button'
                        onClick={handleReset}
                        disabled={disabled}
                        className='inline-flex h-10 items-center gap-2 rounded-lg px-3 text-sm font-medium text-body hover:text-black disabled:opacity-50'
                    >
                        <FaRotateLeft size={12} /> Reset
                    </button>
                )}
            </div>
            {invalidRange && (
                <p className='mt-2 text-xs text-danger'>
                    The From date must be on or before the To date.
                </p>
            )}
        </form>
    );
};

export default RefundAuditFilterBar;
